import React, { useContext } from 'react'
import { CaseDetailContext } from './CaseDetailContext'

/**
 * ProgressTracker — right-sidebar widget matching Scale's screenshot exactly.
 *
 * Layout per party (Plaintiff / Defendant / additional parties):
 *   ╭──────────────────────────────────────────────╮
 *   │ Plaintiff: {name}                            │
 *   │  • Onboarding              {status text}     │
 *   │  • Payment                 {status text}     │
 *   │  • Mediation Statement     {status text}     │
 *   ╰──────────────────────────────────────────────╯
 *
 * Status text is the human string from BE (e.g. "6 days overdue",
 * "No Invoice sent"). The activity log is rendered separately by
 * <ActivityLog />.
 */

// Hex values match Cases.jsx STATUS_COLOR_MAP so the dots render
// identically on the cases list and the case-detail sidebar. 'grey'
// (unknown / not started) is rendered as amber by convention.
const COLOR_HEX = {
    green: '#22c55e',
    yellow: '#f59e0b',
    red: '#ef4444',
    grey: '#f59e0b',
}

const STATUS_FALLBACK = {
    onboarding: { green: 'Complete', yellow: 'Due Soon', red: 'Overdue', grey: 'Not started' },
    payment: { green: 'Paid', yellow: 'Due Soon', red: 'No Invoice sent', grey: 'No Invoice sent' },
    brief: { green: 'Submitted', yellow: 'Due Soon', red: 'Overdue', grey: 'Not started' },
    keyDocs: { green: 'Submitted', yellow: 'Due Soon', red: 'Missing', grey: 'Not submitted' },
}

function StatusDot({ color }) {
    const hex = COLOR_HEX[color] || COLOR_HEX.red
    return <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: hex }} />
}

function StatusItem({ label, status, color }) {
    return (
        <div className="flex items-center justify-between py-1">
            <div className="flex items-center gap-2">
                <StatusDot color={color} />
                <span className="text-xs text-gray-800">{label}</span>
            </div>
            <span className="text-xs text-gray-500">{status}</span>
        </div>
    )
}

function PartyProgress({ title, party, briefLabel = 'Brief', isOdrMediator = false }) {
    if (!party) return null
    // Scale parity (convertToArrayForCaseBullets): non-ODR mediators see
    // Onboarding → Payment → Brief; ODR mediators see Onboarding → Payment
    // → Key Documents (brief is filtered out, key docs added).
    const allItems = [
        { label: 'Onboarding', color: party.onboardingColor || 'grey', status: party.onboardingStatus || STATUS_FALLBACK.onboarding[party.onboardingColor || 'grey'] },
        { label: 'Payment', color: party.paymentColor || 'grey', status: party.paymentStatus || STATUS_FALLBACK.payment[party.paymentColor || 'grey'] },
        !isOdrMediator && { label: briefLabel, color: party.briefColor || 'grey', status: party.briefStatus || STATUS_FALLBACK.brief[party.briefColor || 'grey'] },
        isOdrMediator && { label: 'Key Documents', color: party.keyDocsColor || 'grey', status: party.keyDocsStatus || STATUS_FALLBACK.keyDocs[party.keyDocsColor || 'grey'] },
    ].filter(Boolean)
    return (
        <div className="mb-6 last:mb-0 border-b border-gray-100 pb-4 last:border-b-0 last:pb-0">
            {/* Scale parity: party title rendered as a gray Badge-style pill. */}
            <span
                className="inline-block px-2.5 py-1 mb-3 rounded-md text-xs font-semibold capitalize bg-gray-100 text-gray-600"
                style={{ fontFamily: 'Inter Tight' }}
            >
                {title}
            </span>
            <div className="space-y-1">
                {allItems.map((it, i) => <StatusItem key={i} {...it} />)}
            </div>
        </div>
    )
}

export default function ProgressTracker() {
    const { caseData, plaintiffPartyLabel, defendantPartyLabel } = useContext(CaseDetailContext)
    const plaintiff = caseData?.plaintiff
    const defendant = caseData?.defendant
    const pid = plaintiff?.client_id || plaintiff?.clientId
    const did = defendant?.client_id || defendant?.clientId
    const briefLabel = caseData?.mediatorSettings?.brief_text || 'Brief'
    const isOdrMediator = !!caseData?.mediator?.is_odr_mediator
        || !!caseData?.mediatorSettings?.is_odr_mediator
        || !!caseData?.mediator_id?.is_odr_mediator

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            {/* Scale parity (ProgressTracker.jsx:163-170): gray-50 header
                with title + subtitle, then p-3 body. */}
            <div className="bg-gray-50 px-5 py-5 border-b border-gray-200">
                <div className="text-base xl:text-lg font-semibold text-gray-900">Case Progress Tracker</div>
                <p className="text-xs text-gray-500 mt-1">Detailed record of all actions pending &amp; taken.</p>
            </div>
            <div className="px-3 py-3">
                {plaintiff ? (
                    <PartyProgress
                        title={`${plaintiffPartyLabel}: ${plaintiff.name || ''}`}
                        party={plaintiff}
                        briefLabel={briefLabel}
                        isOdrMediator={isOdrMediator}
                    />
                ) : null}
                {(plaintiff?.additionalParties || []).map((p, i) => (
                    <PartyProgress
                        key={`p-add-${i}`}
                        title={`${plaintiffPartyLabel} ${p.position || 'Additional'}: ${p.client?.name || p.name || ''}`}
                        party={p}
                        briefLabel={briefLabel}
                        isOdrMediator={isOdrMediator}
                    />
                ))}
                {defendant ? (
                    <PartyProgress
                        title={`${defendantPartyLabel}: ${defendant.name || ''}`}
                        party={defendant}
                        briefLabel={briefLabel}
                        isOdrMediator={isOdrMediator}
                    />
                ) : null}
                {(defendant?.additionalParties || []).map((p, i) => (
                    <PartyProgress
                        key={`d-add-${i}`}
                        title={`${defendantPartyLabel} ${p.position || 'Additional'}: ${p.client?.name || p.name || ''}`}
                        party={p}
                        briefLabel={briefLabel}
                        isOdrMediator={isOdrMediator}
                    />
                ))}
                {!plaintiff && !defendant && (
                    <div className="text-xs text-gray-400 italic px-2">No parties on this case yet.</div>
                )}
            </div>
        </div>
    )
}
